import { Component, Inject } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface CustomSnackbarData {
  message: string;
  icon: string;
  type: 'success' | 'error' | 'info';
  trace?: Record<string, unknown>;
}

@Component({
  selector: 'custom-snackbar',
  template: `
    <div class="flex flex-row items-center gap-3 rounded-lg bg-white p-4 shadow-lg border border-gray-200 min-h-[60px]">
      <!-- Icon -->
      <mat-icon [svgIcon]="data.icon" [class]="getIconClass()" class="flex-shrink-0"> </mat-icon>

      <!-- Message -->
      <span class="flex-1 text-gray-800 font-medium leading-relaxed">{{ data.message }}</span>

      <!-- Action Buttons -->
      <div class="flex flex-row items-center gap-1">
        @if (data.trace && data.type === 'error') {
          <button
            (click)="copyTrace()"
            matTooltip="Copy error info to clipboard"
            matTooltipPosition="above"
            class="flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
          >
            <mat-icon svgIcon="heroicons_solid:document-duplicate" class="w-4 h-4"></mat-icon>
          </button>
        }

        <!-- Close Button -->
        <button
          (click)="dismiss()"
          matTooltip="Dismiss notification"
          matTooltipPosition="above"
          class="flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          <mat-icon svgIcon="heroicons_solid:x-mark" class="w-4 h-4"></mat-icon>
        </button>
      </div>
    </div>
  `,
  imports: [MatIconModule, MatTooltipModule],
  standalone: true,
})
export class CustomSnackbarComponent {
  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: CustomSnackbarData,
    private snackBarRef: MatSnackBarRef<CustomSnackbarComponent>
  ) {}

  getIconClass(): string {
    switch (this.data.type) {
      case 'success':
        return 'text-teal-600! w-6 h-6';
      case 'error':
        return 'text-rose-500! w-6 h-6';
      case 'info':
        return 'text-indigo-500! w-6 h-6';
      default:
        return 'text-gray-500! w-6 h-6';
    }
  }

  dismiss() {
    this.snackBarRef.dismiss();
  }

  async copyTrace() {
    if (!this.data.trace) return;

    try {
      const traceString = JSON.stringify(this.data.trace, null, 2);
      await navigator.clipboard.writeText(traceString);
    } catch (error) {
      console.error('Failed to copy error info to clipboard:', error);
    }
  }
}
